import React, { useState, useEffect, Fragment, useMemo } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import '../css/index.scss';
import UserMenu from '../containers/UserMenu';
import { Alert } from 'reactstrap';
import { useStores } from '../hooks/use-stores';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { FlexDiv, TextTiny } from '@awarego-ui/components';
import styled, { css } from 'styled-components';
import { publicHRASubstrings } from '../utils/constants';

const Logo = styled.div`
    img {
        max-height: 40px;
        max-width: 130px;
    }
    padding: 24px;
    position: relative;
    margin-right: 32px;
    ${(props) =>
        props.noMargin &&
        css`
            margin: 0;
        `}
    cursor: pointer;
`;

function Layout({ sendFeedback, handleSignout, switchLanguage }) {
    const { store, brandingStore, authStore } = useStores();

    const { logo, account_name, favicon, brandingLoaded, privacyUrl } =
        brandingStore;
    const { layoutTitle } = store;
    const { isAuthenticated } = authStore;

    // Subscribe to assessment ns so we re-render when it finishes loading after a
    // language switch. The previous exists('assessment:…') check raced: it ran
    // before da/assessment.json was loaded and never recomputed, leaving the
    // English package name (e.g. "Office security") in the header.
    const { t, i18n, ready } = useTranslation(['common', 'assessment']);

    const displayLayoutTitle = useMemo(() => {
        if (!layoutTitle) return '';
        if (!ready) return layoutTitle;
        if (i18n.exists(layoutTitle, { ns: 'assessment' })) {
            return t(layoutTitle, { ns: 'assessment' });
        }
        if (i18n.exists(layoutTitle, { ns: 'common' })) {
            return t(layoutTitle);
        }
        return layoutTitle;
    }, [layoutTitle, t, i18n, ready]);

    const [notification, setNotification] = useState(false);
    const [notificationType, setNotificationType] = useState('warning');
    const [notificationMessage, setNotificationMessage] =
        useState('All is good');
    const [notificationTimestamp, setNotificationTimestamp] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    const NOTIFICATION_DISMISSED_KEY = 'awarego.notification.my.dismissed';

    const location = useLocation();

    const infosecPages = useMemo(() => {
        const path = location.pathname;
        return (
            path.startsWith('/mini-hra') ||
            path.startsWith('/h/') ||
            publicHRASubstrings.some((x) => path.startsWith(`/${x}`))
        );
    }, [location.pathname]);

    const miniHRAtype = useMemo(() => {
        const type = publicHRASubstrings.find((x) =>
            location.pathname.startsWith(`/${x}`)
        );
        brandingStore.setDefaultMiniHraType(type);
        return type;
    }, [location.pathname]);

    const miniHRARoot = useMemo(() => {
        return publicHRASubstrings.find((x) => location.pathname === `/${x}`);
    }, [location.pathname]);

    const contentVerticallyCentered = !isAuthenticated;
    const date = new Date();

    const dismissNotification = () => {
        if (
            notificationTimestamp != null &&
            typeof localStorage !== 'undefined'
        ) {
            localStorage.setItem(
                NOTIFICATION_DISMISSED_KEY,
                String(notificationTimestamp)
            );
        }
        setNotification(false);
    };

    useEffect(() => {
        let cancelled = false;
        let expireTimer;

        fetch('https://resources.awarego.com/notification-my.json')
            .then((response) => response.json())
            .then((jsonData) => {
                if (cancelled) return;

                const timestamp =
                    jsonData.timestamp != null
                        ? String(jsonData.timestamp)
                        : null;
                const expiresAt =
                    jsonData.expiresAt != null
                        ? Number(jsonData.expiresAt)
                        : null;
                const dismissed =
                    typeof localStorage !== 'undefined'
                        ? localStorage.getItem(NOTIFICATION_DISMISSED_KEY)
                        : null;
                const nowSec = Date.now() / 1000;
                const notExpired = !expiresAt || nowSec < expiresAt;
                const shouldShow =
                    !!jsonData.status &&
                    (!timestamp || timestamp !== dismissed) &&
                    notExpired;

                setNotification(shouldShow);
                setNotificationType(jsonData.type);
                setNotificationMessage(jsonData.message);
                setNotificationTimestamp(timestamp);

                if (shouldShow && expiresAt) {
                    const msUntilExpire = expiresAt * 1000 - Date.now();
                    if (msUntilExpire > 0) {
                        expireTimer = setTimeout(() => {
                            if (!cancelled) setNotification(false);
                        }, msUntilExpire);
                        if (cancelled) clearTimeout(expireTimer);
                    }
                }
            })
            .catch((error) => {
                console.error(error);
            });

        setBodyBackground();

        return () => {
            cancelled = true;
            if (expireTimer) clearTimeout(expireTimer);
        };
    }, []);

    const setBodyBackground = () => {
        const currentURL = window.location.pathname;

        if (
            currentURL.includes('mini-hra') ||
            currentURL.startsWith('/h/') ||
            publicHRASubstrings.some((x) => currentURL.includes(x))
        ) {
            document.body.classList.add('infosec');
        }
    };

    const toggleNavbar = () => {
        setIsOpen(!isOpen);
    };

    const updateFavicon = (logo) => {
        const favicon = document.getElementById('favicon');
        favicon.href = logo;
        return true;
    };

    const logoContainer = (brandingLoaded || infosecPages) && (
        <Link to="/">
            <img src={logo} alt={t('logoAlt', account_name)} />
        </Link>
    );

    return (
        <Fragment>
            {brandingLoaded && updateFavicon(favicon) && (
                <Helmet
                    titleTemplate={'%s | ' + account_name}
                    defaultTitle={`${account_name}`}
                />
            )}
            <div
                className={classnames('layout_wrapper', {
                    ['infosec_bg']: infosecPages,
                })}
            >
                <a href="#main-content" className="skip-link">
                    {t('Skip to main content')}
                </a>
                {!isAuthenticated || infosecPages ? (
                    miniHRAtype == null ||
                    (miniHRAtype != null && !miniHRARoot && (
                        <>
                            <Logo>{logoContainer}</Logo>
                        </>
                    ))
                ) : (
                    <Fragment>
                        <div
                            onClick={() => toggleNavbar()}
                            className={
                                isOpen
                                    ? 'overlay-black visible'
                                    : 'overlay-black'
                            }
                        />
                        {isAuthenticated && (
                            <ul
                                id="navToggle"
                                onClick={() => toggleNavbar()}
                                className="burger slide"
                            >
                                <li />
                                <li />
                                <li />
                            </ul>
                        )}
                        <header className={isOpen ? 'slide open' : 'slide'}>
                            <div className="container full">
                                <div className="header">
                                    {brandingLoaded ? (
                                        <Link to="/" className="logo_box">
                                            <img
                                                src={logo}
                                                alt={t('logoAlt', account_name)}
                                            />
                                        </Link>
                                    ) : (
                                        <span className="logo_box"></span>
                                    )}
                                    <div className="header-title">
                                        {displayLayoutTitle}
                                    </div>
                                    <div className="close">
                                        <img
                                            src="/static/img/icon-close.png"
                                            alt={t('Close the menu')}
                                        />
                                    </div>

                                    <UserMenu
                                        sendFeedback={sendFeedback}
                                        handleSignout={handleSignout}
                                        switchLanguage={switchLanguage}
                                    />
                                </div>
                            </div>
                        </header>
                        {notification && (
                            <Alert
                                color={notificationType}
                                isOpen={notification}
                                toggle={dismissNotification}
                            >
                                {notificationMessage}
                            </Alert>
                        )}

                        <div className="mobile-header">
                            <div className="logo_mobile">
                                {brandingLoaded && (
                                    <Link to="/" className="logo_box">
                                        <img
                                            src={logo}
                                            alt={t('logoAlt', account_name)}
                                        />
                                    </Link>
                                )}
                            </div>
                        </div>
                    </Fragment>
                )}

                <main
                    id="main-content"
                    className={classnames('container-wrapper', {
                        'd-flex container-wrapper__spacy':
                            contentVerticallyCentered,
                    })}
                    tabIndex={-1}
                >
                    <Outlet />
                </main>

                {!infosecPages && (
                    <footer>
                        <FlexDiv justifyCenter my={24} alignCenter>
                            <TextTiny lighter>
                                <a
                                    href={privacyUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('Privacy Policy')}
                                </a>{' '}
                                &nbsp;&#183; © {date.getFullYear()}{' '}
                                {(brandingLoaded && account_name) || 'AwareGO'}
                            </TextTiny>
                        </FlexDiv>
                    </footer>
                )}
            </div>
        </Fragment>
    );
}

export default observer(Layout);
